// src/components/financeOfficer/CreateRequisitionForm.jsx

import React, { useState } from "react";
import { toast } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { FiFileText, FiDollarSign, FiHome, FiInfo, FiX, FiSave, FiLoader } from "react-icons/fi";

const CreateRequisitionForm = ({ onClose, onSubmit }) => {
    const [formData, setFormData] = useState({
        items: "",
        amount: "",
        department: "Procurement",
        purpose: "",
    });
    const [loading, setLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        if (!formData.items || !formData.amount || !formData.department) {
            toast.error("Please fill in all required fields.");
            setLoading(false);
            return;
        }

        try {
            // Mock API call delay
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            onSubmit(formData);
            
            toast.success("Requisition created successfully!");
        } catch (error) {
            console.error("Failed to create requisition:", error);
            toast.error("Failed to create requisition. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-5">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-white/20 rounded-lg">
                            <FiFileText className="h-6 w-6 text-white" />
                        </div>
                        <h2 className="text-xl font-bold text-white">Create New Requisition</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg text-white hover:bg-white/20 transition-colors"
                    >
                        <FiX className="h-5 w-5" />
                    </button>
                </div>
            </div>

            {/* Form Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
                <div>
                    <label htmlFor="items" className="block text-sm font-medium text-gray-700 mb-1">
                        Items/Description <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiFileText className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            name="items"
                            id="items"
                            value={formData.items}
                            onChange={handleChange}
                            className="pl-10 block w-full border border-gray-300 rounded-lg p-3 shadow-sm focus:border-orange-500 focus:ring-orange-500 transition-all"
                            placeholder="e.g., Medical Kits, Office Supplies"
                            required
                        />
                    </div>
                </div>
                
                <div>
                    <label htmlFor="amount" className="block text-sm font-medium text-gray-700 mb-1">
                        Amount (SSP) <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiDollarSign className="h-5 w-5 text-gray-400" />
                        </div>
                        <input
                            type="number"
                            name="amount"
                            id="amount"
                            value={formData.amount}
                            onChange={handleChange}
                            className="pl-10 block w-full border border-gray-300 rounded-lg p-3 shadow-sm focus:border-orange-500 focus:ring-orange-500 transition-all"
                            placeholder="e.g., 20500"
                            required
                        />
                    </div>
                </div>
                
                <div>
                    <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-1">
                        Department <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiHome className="h-5 w-5 text-gray-400" />
                        </div>
                        <select
                            name="department"
                            id="department"
                            value={formData.department}
                            onChange={handleChange}
                            className="pl-10 block w-full border border-gray-300 rounded-lg p-3 shadow-sm focus:border-orange-500 focus:ring-orange-500 transition-all appearance-none bg-white"
                            required
                        >
                            <option value="Procurement">Procurement</option>
                            <option value="Programs">Programs</option>
                            <option value="Logistics">Logistics</option>
                            <option value="HR">HR</option>
                            <option value="Assets">Assets</option>
                        </select>
                    </div>
                </div>
                
                <div>
                    <label htmlFor="purpose" className="block text-sm font-medium text-gray-700 mb-1">
                        Purpose
                    </label>
                    <div className="relative">
                        <div className="absolute top-3 left-0 pl-3 flex items-start pointer-events-none">
                            <FiInfo className="h-5 w-5 text-gray-400" />
                        </div>
                        <textarea
                            name="purpose"
                            id="purpose"
                            rows="3"
                            value={formData.purpose}
                            onChange={handleChange}
                            className="pl-10 block w-full border border-gray-300 rounded-lg p-3 shadow-sm focus:border-orange-500 focus:ring-orange-500 transition-all resize-none"
                            placeholder="Briefly describe the purpose of this requisition."
                        />
                    </div>
                </div>
                
                <div className="flex justify-end gap-3 pt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors flex items-center"
                    >
                        <FiX className="mr-2 -ml-1 h-4 w-4" />
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="px-5 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all flex items-center disabled:opacity-70"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <FiLoader className="mr-2 -ml-1 h-4 w-4 animate-spin" />
                                Creating...
                            </>
                        ) : (
                            <>
                                <FiSave className="mr-2 -ml-1 h-4 w-4" />
                                Create Requisition
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreateRequisitionForm;